import { useEffect, useMemo, useState } from "react";
import "./AccountMobileMenu.css";
import Section from "../../../components/base/Section";
import Layout from "../../../components/layouts/Layout";
import { getUser, hasRole, setPage, useEnvironment } from "../../../core/Global";
import Icon from "../../../components/base/Icon";
import Avatar from "../../../components/base/Avatar";
import { MobileMenuItem } from "../../../components/mobile/MobileMenuItem";
import { FontAwesomeIcons } from "../../../core/Types";
import { useTapToPay } from "../../../hooks/useTapToPay";
import { ISideMenuItem } from "../../../components/layouts/aside/SideMenu";
import { useCRMSideMenuItems } from "../../crm/views/CRMSideMenu";
import { useVoiceSideMenuItems } from "../../voice/views/VoiceSideMenu";
import { useAccountsSideMenuItems } from "../../accounts/views/AccountsSideMenu";
import { useInventorySideMenuItems } from "../../inventory/views/InventorySideMenu";
import { usePreferencesSideMenuItems } from "../../system/settings/views/PreferencesSideMenu";
import ModuleMenuDrawer from "./ModuleMenuDrawer";
import AccountEditMobile from "./AccountEditMobile";
import AccountEditTimezoneMobile from "./AccountEditTimezoneMobile";
import AccountUsernameMobile from "./AccountUsernameMobile";
import AccountPasswordMobile from "./AccountPasswordMobile";
import ThemeMobile from "./ThemeMobile";


interface AppModule {
    key: string
    name: string
    icon: FontAwesomeIcons
    color: string
    items: ISideMenuItem[]
}


interface AccountRow {
    id: string
    name: string
    icon: FontAwesomeIcons
    color: string
    danger?: boolean
}


export default function AccountMobileMenu() {

    const user = getUser()
    const env = useEnvironment()

    const [action, actionSet] = useState<string>()

    // Tap to Pay entry only appears on an eligible iOS phone whose account has
    // gateway keys configured on the server. No keys → no Tap to Pay anywhere.
    // Once set up, the row opens Apple's merchant education screens (req 4.2);
    // before that it goes straight to setup / T&C acceptance (req 3.6).
    const {
        eligible: tapToPayEligible,
        gatewayConfigured: tapToPayGatewayConfigured,
        enabled: tapToPayEnabled,
        startSetup: startTapToPaySetup,
        openEducation: openTapToPayEducation,
    } = useTapToPay()
    const showTapToPay = tapToPayEligible && tapToPayGatewayConfigured

    useEffect(() => {
        setPage("Account")
    }, [])

    // This page is full-bleed with no top header. Hide the global mobile header
    // and reclaim its reserved space while the Account page is mounted.
    useEffect(() => {
        document.body.classList.add("mobile-fullpage-account")
        return () => {
            document.body.classList.remove("mobile-fullpage-account")
        }
    }, [])

    // Role-filtered sidebar items for each module (same hooks the desktop uses).
    const crmItems = useCRMSideMenuItems()
    const voiceItems = useVoiceSideMenuItems()
    const accountsItems = useAccountsSideMenuItems()
    const inventoryItems = useInventorySideMenuItems()
    const systemItems = usePreferencesSideMenuItems()

    const groups = useMemo<AccountRow[][]>(() => [
        [
            { id: "account", name: "Account", icon: "user", color: "#007aff" },
            { id: "username", name: "Username", icon: "at", color: "#34c759" },
            { id: "password", name: "Password", icon: "lock", color: "#8e8e93" },
        ],
        [
            { id: "timezone", name: "Timezone", icon: "clock", color: "#ff9500" },
            { id: "theme", name: "Theme", icon: "circle-half-stroke", color: "#af52de" },
        ],
        ...(showTapToPay ? [[
            { id: "taptopay", name: "Tap to Pay on iPhone", icon: "mobile-screen" as FontAwesomeIcons, color: "#5856d6" },
        ]] : []),
        [
            { id: "logout", name: "Sign out", icon: "arrow-right-from-bracket", color: "#ff3b30", danger: true },
        ],
    ], [showTapToPay])


    // Main modules (like the module bar). Tapping one opens a drawer showing the
    // whole module sidebar with its grouping. Access control comes for free: each
    // module's hook returns only the items the user may see; a module shows only
    // when it has at least one such item (CRM/Inventory also gated by HasCRM).
    const modules = useMemo<AppModule[]>(() => {
        const list: AppModule[] = []
        const has = (items: ISideMenuItem[]) => items.some(i => !i.seperator && !!i.link)

        if (hasRole("HasCRM") && has(crmItems))
            list.push({ key: "crm", name: "CRM", icon: "briefcase", color: "#007aff", items: crmItems })

        if (has(voiceItems))
            list.push({ key: "voice", name: "Voice", icon: "phone-volume", color: "#34c759", items: voiceItems })

        if (has(accountsItems))
            list.push({ key: "accounts", name: "Accounts", icon: "user", color: "#ff9500", items: accountsItems })

        if (hasRole("HasCRM") && has(inventoryItems))
            list.push({ key: "inventory", name: "Inventory", icon: "barcode-scan", color: "#af52de", items: inventoryItems })

        // The System module's Account group (Account Overview / Edit Account /
        // Security & Login) is already covered by the main Settings tab in a
        // different form, so hide those on mobile.
        const systemHidden = ["account-overview", "account-edit-account", "account-security"]
        const sysItems = systemItems.filter(i => !systemHidden.includes(i.id ?? ""))
        if (has(sysItems))
            list.push({ key: "system", name: "System", icon: "gear", color: "#8e8e93", items: sysItems })

        return list
    }, [crmItems, voiceItems, accountsItems, inventoryItems, systemItems])

    const [moduleDrawer, moduleDrawerSet] = useState<AppModule>()

    const rowClick = (m: AccountRow) => {
        if (m.id == "logout") window.openModal("modal-signout")
        // Straight into the native flow — no intermediate drawer.
        else if (m.id == "taptopay") {
            if (tapToPayEnabled) openTapToPayEducation()
            else startTapToPaySetup()
        }
        else actionSet(m.id)
    }

    const firstGroup = groups[0]
    const restGroups = groups.slice(1)

    const renderRow = (m: AccountRow, onClick: () => void) => (
        <MobileMenuItem key={m.id} onClick={onClick}>
            <div className={`tg-row ${m.danger ? "tg-row-danger" : ""}`}>
                <div className="tg-row-icon" style={{ backgroundColor: m.color }}>
                    <Icon icon={m.icon} type="solid" size={15} />
                </div>
                <div className="tg-row-body">
                    <span className="tg-row-label">{m.name}</span>
                    {!m.danger &&
                        <Icon icon="chevron-right" size={13} className="tg-row-chevron" />
                    }
                </div>
            </div>
        </MobileMenuItem>
    )


    return (
        <Layout wide>

            <Section fluid fit>
            <div className="tg-account">

                <div className="tg-profile">
                    <Avatar className="tg-profile-avatar" avatarUrl={user?.avatarUrl}
                        textSize={44} fullName={user?.fullName} width={96} />
                    <div className="tg-profile-name">{user?.fullName}</div>
                    <div className="tg-profile-sub">
                        {user?.phoneFriendly && <span>{user.phoneFriendly}</span>}
                        {user?.phoneFriendly && user?.username && <span className="tg-dot">·</span>}
                        {user?.username && <span>@{user.username}</span>}
                    </div>
                </div>

                <div className="tg-group">
                    {firstGroup.map(m => renderRow(m, () => rowClick(m)))}
                </div>

                {modules.length > 0 &&
                    <div className="tg-group">
                        {modules.map(m =>
                            <MobileMenuItem key={m.key} onClick={() => moduleDrawerSet(m)}>
                                <div className="tg-row">
                                    <div className="tg-row-icon" style={{ backgroundColor: m.color }}>
                                        <Icon icon={m.icon} type="solid" size={15} />
                                    </div>
                                    <div className="tg-row-body">
                                        <span className="tg-row-label">{m.name}</span>
                                        <Icon icon="chevron-right" size={13} className="tg-row-chevron" />
                                    </div>
                                </div>
                            </MobileMenuItem>
                        )}
                    </div>
                }

                {restGroups.map((group, gi) =>
                    <div key={gi} className="tg-group">
                        {group.map(m => renderRow(m, () => rowClick(m)))}
                    </div>
                )}

                <div className="tg-version">
                    Version {env.versionName}
                </div>

            </div>
            </Section>


            {
                action == "account" &&
                <AccountEditMobile
                    onClose={() => actionSet(undefined)} />
            }

            {
                action == "username" &&
                <AccountUsernameMobile
                    onClose={() => actionSet(undefined)} />
            }

            {
                action == "password" &&
                <AccountPasswordMobile
                    onClose={() => actionSet(undefined)} />
            }

            {
                action == "timezone" &&
                <AccountEditTimezoneMobile
                    onClose={() => actionSet(undefined)} />
            }


            {
                action == "theme" &&
                <ThemeMobile
                    onClose={() => actionSet(undefined)} />
            }

            {
                moduleDrawer &&
                <ModuleMenuDrawer
                    title={moduleDrawer.name}
                    titleIcon={moduleDrawer.icon}
                    items={moduleDrawer.items}
                    onClose={() => moduleDrawerSet(undefined)} />
            }

        </Layout>
    )
}
